/**
 * Robust Timezone Service for TrackerV2 using date-fns-tz
 * 
 * ARCHITECTURE:
 * - All data stored in UTC in database
 * - Frontend ↔ Backend communication always in UTC ISO format
 * - User timezone applied only in presentation layer
 * - Filtering and DB queries always in UTC
 * - No hardcoded locales - uses date-fns-tz for reliable timezone handling
 */

import { toZonedTime, fromZonedTime, format } from 'date-fns-tz';

export class TimezoneService {
  private userTimezone: string;

  constructor(userTimezone: string = Intl.DateTimeFormat().resolvedOptions().timeZone) {
    this.userTimezone = userTimezone;
  }

  /**
   * Set user's timezone (from user settings/database)
   */
  public setUserTimezone(timezone: string): void {
    this.userTimezone = timezone;
  }

  /**
   * Get current user's timezone
   */
  public getUserTimezone(): string {
    return this.userTimezone;
  }

  /**
   * Convert UTC date string to user's local time
   * Replaces hackish new Date(utcDate.toLocaleString(...)) approach
   * @param utcDateString - UTC date string (ISO format)
   * @returns Date object in user's timezone
   */
  public convertUTCToUserTime(utcDateString: string): Date {
    // eslint-disable-next-line no-restricted-syntax
    const utcDate = new Date(utcDateString);
    return toZonedTime(utcDate, this.userTimezone);
  }

  /**
   * Convert user's local time to UTC
   * @param localDate - Date in user's timezone
   * @returns UTC date string (ISO format)
   */
  public convertUserTimeToUTC(localDate: Date): string {
    // Convert the local date to UTC using the user's timezone
    const utcDate = fromZonedTime(localDate, this.userTimezone);
    return utcDate.toISOString();
  }

  /**
   * Convert display date (YYYY-MM-DD) to UTC for API calls
   * FIXED: Uses explicit T00:00:00 format for reliable parsing
   * @param displayDate - Date string in YYYY-MM-DD format
   * @returns UTC date string for API
   */
  public convertDisplayDateToUTC(displayDate: string): string {
    // Use explicit ISO format to avoid browser/Node.js parsing differences
    // eslint-disable-next-line no-restricted-syntax
    const zonedDate = fromZonedTime(new Date(`${displayDate}T00:00:00`), this.userTimezone);
    return zonedDate.toISOString();
  }

  /**
   * Format time for display (24-hour format)
   * @param date - Date object (can be UTC or zoned)
   * @param formatStr - Format string (default: HH:mm for 24-hour)
   * @returns Formatted time string
   */
  public formatUserTime(date: Date, options?: { hour?: string; minute?: string }): string {
    // Convert to user timezone if it's a UTC date
    const zonedDate = toZonedTime(date, this.userTimezone);
    
    // Use date-fns format for consistent formatting
    const formatStr = options?.hour === '2-digit' && options?.minute === '2-digit' ? 'HH:mm' : 'HH:mm';
    return format(zonedDate, formatStr, { timeZone: this.userTimezone });
  }

  /**
   * Format date for display
   * @param date - Date object
   * @param options - Formatting options
   * @returns Formatted date string
   */
  public formatUserDate(date: Date, options?: Intl.DateTimeFormatOptions): string {
    // Convert to user timezone if it's a UTC date
    const zonedDate = toZonedTime(date, this.userTimezone);
    
    // Map common options to date-fns format strings
    let formatStr = 'yyyy-MM-dd'; // Default
    
    if (options?.weekday && options?.month && options?.day) {
      if (options.weekday === 'long') {
        formatStr = 'EEEE, MMM d'; // "Monday, Sep 27"
      } else if (options.weekday === 'short') {
        formatStr = 'EEE, MMM d, yyyy'; // "Mon, Sep 27, 2025"
      }
    } else if (options?.month && options?.year) {
      if (options.month === 'long') {
        formatStr = 'MMMM yyyy'; // "September 2025"
      } else if (options.month === 'short') {
        formatStr = 'MMM'; // "Sep"
      }
    } else if (options?.day && options?.month) {
      formatStr = 'd MMM'; // "27 Sep"
    }
    
    return format(zonedDate, formatStr, { timeZone: this.userTimezone });
  }

  /**
   * Format datetime for display (combines date and time)
   * @param utcDateString - UTC date string
   * @returns Formatted datetime string
   */
  public formatUserDateTime(utcDateString: string): string {
    const zonedDate = this.convertUTCToUserTime(utcDateString);
    return format(zonedDate, 'yyyy-MM-dd HH:mm', { timeZone: this.userTimezone });
  }

  /**
   * Get current date in user's timezone (for date pickers, etc.)
   * @returns Date string in YYYY-MM-DD format
   */
  public getCurrentUserDate(): string {
    // eslint-disable-next-line no-restricted-syntax
    const now = new Date();
    const zonedNow = toZonedTime(now, this.userTimezone);
    return format(zonedNow, 'yyyy-MM-dd');
  }

  /**
   * Get current time in user's timezone
   * @returns Time string in HH:mm format
   */
  public getCurrentUserTime(): string {
    // eslint-disable-next-line no-restricted-syntax
    const now = new Date();
    const zonedNow = toZonedTime(now, this.userTimezone);
    return format(zonedNow, 'HH:mm');
  }

  /**
   * Get yesterday's date in user's timezone
   * @returns Date string in YYYY-MM-DD format
   */
  public getYesterdayUserDate(): string {
    // eslint-disable-next-line no-restricted-syntax
    const now = new Date();
    // eslint-disable-next-line no-restricted-syntax
    const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000);
    const zonedYesterday = toZonedTime(yesterday, this.userTimezone);
    return format(zonedYesterday, 'yyyy-MM-dd');
  }

  /**
   * Check if a date is today in user's timezone
   * @param date - Date to check
   * @returns True if date is today in user timezone
   */
  public isToday(date: Date | string): boolean {
    // eslint-disable-next-line no-restricted-syntax
    const checkDate = typeof date === 'string' ? new Date(date) : date;
    const zonedDate = toZonedTime(checkDate, this.userTimezone);
    // eslint-disable-next-line no-restricted-syntax
    const today = toZonedTime(new Date(), this.userTimezone);
    
    return format(zonedDate, 'yyyy-MM-dd') === format(today, 'yyyy-MM-dd');
  }

  /**
   * Check if a date is yesterday in user's timezone
   * @param date - Date to check
   * @returns True if date is yesterday in user timezone
   */
  public isYesterday(date: Date | string): boolean {
    // eslint-disable-next-line no-restricted-syntax
    const checkDate = typeof date === 'string' ? new Date(date) : date;
    const zonedDate = toZonedTime(checkDate, this.userTimezone);
    // eslint-disable-next-line no-restricted-syntax
    const yesterday = toZonedTime(new Date(Date.now() - 24 * 60 * 60 * 1000), this.userTimezone);
    
    return format(zonedDate, 'yyyy-MM-dd') === format(yesterday, 'yyyy-MM-dd');
  }

  /**
   * Debug function to log timezone conversion
   */
  public debugConversion(utcDateString: string, label: string = ''): void {
    // eslint-disable-next-line no-restricted-syntax
    const utcDate = new Date(utcDateString);
    const zonedDate = this.convertUTCToUserTime(utcDateString);
    const formatted = this.formatUserDateTime(utcDateString);
    
    console.log(`🕐 TIMEZONE DEBUG ${label}:`, {
      utc: utcDateString,
      userTimezone: this.userTimezone,
      zonedDate: zonedDate.toISOString(),
      formatted: formatted,
      isToday: this.isToday(utcDate),
      isYesterday: this.isYesterday(utcDate)
    });
  }
}

// Create default instance for backward compatibility
// Note: In a real app, you'd want to create per-user instances
const defaultTimezoneService = new TimezoneService();

// Export both class and default instance
export { defaultTimezoneService as timezoneService };
export default defaultTimezoneService;
